import Vue from 'vue';
import Vuex from 'vuex';

import { api } from '@bit/duncank.work.api';
import apiErrors from '@bit/duncank.work.utils.api-errorhandling';
import Auth from '@bit/duncank.work.store.auth';
import Language from '@bit/duncank.work.store.language';
import Toaster from '@bit/duncank.work.plugins.toaster';
import capitalize from 'lodash.capitalize';
import Products from '@/store/Products';

Vue.use(Toaster);
Vue.use(Vuex);

export default new Vuex.Store({
    modules: {
        Auth,
        Language,
        Products,
    },
    state: {
        app: {
            loading: false,
            loading_names: [],
            loading_fullscreen: {
                show: false,
                title: '',
                message: '',
            },
            languages: [
                { id: 'nl', name: 'Nederlands' },
                { id: 'en', name: 'English' },
            ],
            update_meta: true,
            base_url: 'https://koningstuin.nl',
            meta: {
                title: '',
                titleDefault: 'Koningstuin BV - Kings Garden Collection®',
                titleSeparator: ' - ',
                description: '',
                descriptionDefault: '',
                image: '',
                imageDefault: 'https://koningstuin.nl/img/icons/android-chrome-512x512.png',
                keywords: '',
                keywordsDefault: '',
                url: '',
            },
            nav_opened: false,
            version: APPLICATION_VERSION,
        },
        api: {
            url: `https://api.koningstuin.nl/${process.env.VUE_APP_API_VERSION}/`,
            file_location: 'https://koningstuin.nl/',
            img_path: 'https://koningstuin.nl',
            headers: {
                'app-version': APPLICATION_VERSION,
            },
        },
        data: {},
    },
    getters: {
        apiUrl: state => state.api.url,

        apiConfig: (state, getters) => {
            const data = state.api;
            data.headers = Object.assign({}, data.headers, getters['Auth/header']);
            return data;
        },

        meta: (state) => {
            const data = state.app.meta;

            return {
                title: data.title,
                titleTemplate: title => (title ? `${title} ${data.titleSeparator} ${data.titleDefault}` : data.titleDefault),
                meta: [
                    // Title
                    { property: 'og:title', content: data.title || data.titleDefault },
                    { name: 'twitter:title', content: data.title || data.titleDefault },
                    { itemprop: 'name', content: data.title || data.titleDefault },

                    // Description
                    { name: 'description', content: data.description || data.descriptionDefault },
                    { name: 'keywords', content: data.keywords || data.keywordsDefault },
                    { property: 'og:description', content: data.description || data.descriptionDefault },
                    { name: 'twitter:description', content: data.description || data.descriptionDefault },
                    { itemprop: 'description', content: data.description || data.descriptionDefault },

                    // Image
                    { property: 'og:image', content: data.image || data.imageDefault },
                    { name: 'twitter:image:src', content: data.image || data.imageDefault },
                    { itemprop: 'image', content: data.image || data.imageDefault },

                    // Sitename
                    { property: 'og:site_name', content: data.titleDefault },

                    // Url
                    { property: 'og:url', content: window.location.href },
                    { name: 'twitter:site', content: window.location.href },

                    // Others
                    { name: 'og:locale', content: state.Language.lang },
                    { name: 'twitter:card', content: 'summary' },
                ],
                link: [
                    { rel: 'canonical', href: window.location.href },
                ],
            };
        },
    },
    mutations: {
        loadStart(state, name) {
            state.app.loading_names.push(name);
            state.app.loading = true;
        },

        loadEnd(state, name) {
            const index = state.app.loading_names.indexOf(name);
            if (index !== -1) {
                state.app.loading_names.splice(index, 1);
            }

            if (state.app.loading_names.length === 0) {
                state.app.loading = false;

                if (state.app.loading_fullscreen && !state.app.loading_fullscreen.message) {
                    state.app.loading_fullscreen.show = false;
                    state.app.loading_fullscreen.title = '';
                    state.app.loading_fullscreen.message = '';
                }
            }
        },

        setData(state, data) {
            state.data = data;
        },

        setMeta(state, { from, data }) {
            if (from === 'router') {
                if (data.meta && data.meta.meta === false) {
                    return false;
                }
                if (!data.meta || typeof data.meta.meta === 'undefined') {
                    data = false;
                } else {
                    data = data.meta.meta;
                }
            }
            const statedata = state.app.meta;
            if (!data || typeof data !== 'object') {
                // reset
                data = {
                    title: '',
                    description: '',
                    image: '',
                    keywords: '',
                };
            }
            Object.assign(statedata, data);
            Object.keys(statedata).forEach((key) => {
                if (typeof statedata[key] === 'function') {
                    Vue.set(statedata, key, capitalize(statedata[key]()));
                }
            });
            state.app.meta = statedata;
            return true;
        },

        setAppData() {
            // console.log(config);
        },

        nav(state, data) {
            if (data.action === 'toggle') {
                state.app.nav_opened = !state.app.nav_opened;
            } else if (data.action === 'close') {
                state.app.nav_opened = false;
            }
        },
    },
    actions: {
        onLogin({ commit, dispatch, getters }, applicationData = {}) {
            return new Promise((resolve) => {
                commit('setAppData', applicationData);

                const user = getters['Auth/user'];
                if (user) {
                    dispatch('Products/setUserInfo', { user });
                }
                resolve();
            });
        },

        onLogout({ commit }) {
            // reset application-contents
            commit('setAppData', { settings: { cdn: '' } });
        },

        getTexts({ commit }) {
            api.get({
                name: 'get_texts',
                endpoint: 'texts/',
                loading: true,
            }).then((r) => {
                if (r.data && r.data.status === 'success') {
                    commit('setData', r.data.results);
                    if (r.data.results.settings) {
                        commit('setMeta', {
                            from: 'config',
                            data: {
                                descriptionDefault: r.data.results.settings.metabeschrijving,
                                keywordsDefault: r.data.results.settings.metatags,
                            },
                        });
                    }
                }
            }).catch((err) => {
                apiErrors.methods.apiErrors(err);
            });
        },
    },
});
